import React, { useEffect, useRef, useState } from 'react';
import { Plus, X, Users, Star, Search, UserCheck } from 'lucide-react';

const EMPTY = () => ({ id: '', first_name: '', last_name: '', email: '', phone: '' });
const GREEN = '#2E6A4C';

/* Formulaire invités avec état local : la saisie ne re-rend pas tout le portail (fluide), le parent est prévenu avec un léger différé. */
export const SocieteGuestsForm = ({ initial, maxGuests = 1, remainingGuests = 0, suggestions = [], onChange, ready = true, searchClients = null }) => {
  const [guests, setGuests] = useState(() => (initial && initial.length ? initial : [EMPTY()]));
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  useEffect(() => {
    if (!searchClients) return;
    clearTimeout(searchTimer.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try { setResults((await searchClients(q)) || []); } catch { setResults([]); }
      setSearching(false);
    }, 250);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  const timer = useRef(null);
  const latest = useRef(guests);
  latest.current = guests;

  const flush = () => { clearTimeout(timer.current); onChange(latest.current); };
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(latest.current), 200);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests]);

  const update = (i, k, v) => setGuests(gs => gs.map((g, j) => (j === i ? { ...g, [k]: v, ...(k !== 'id' ? { id: '' } : {}) } : g)));
  const addGuest = () => guests.length < maxGuests && setGuests(gs => [...gs, EMPTY()]);
  const removeGuest = (i) => setGuests(gs => (gs.length > 1 ? gs.filter((_, j) => j !== i) : [EMPTY()]));
  const pickSuggestion = (sg) => {
    if (guests.some(g => g.id === sg.id)) return;
    const idx = guests.findIndex(g => !g.first_name && !g.last_name);
    const val = { id: sg.id || '', first_name: sg.first_name || '', last_name: sg.last_name || '', email: sg.email || '', phone: sg.phone || '' };
    if (idx >= 0) setGuests(gs => gs.map((g, j) => (j === idx ? val : g)));
    else if (guests.length < maxGuests) setGuests(gs => [...gs, val]);
  };
  const canAdd = guests.length < maxGuests;

  return (
    <div className="space-y-2" data-testid="societe-guest-form" onBlur={flush}>
      {searchClients && (
        <div className="relative" data-testid="societe-guest-search">
          <label className="text-[11px] font-medium text-neutral-500 block mb-1">Inviter un ami qui a déjà un compte</label>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Rechercher par nom ou prénom…"
              className="w-full h-8 text-sm border rounded pl-7 pr-2"
              data-testid="societe-guest-search-input"
            />
          </div>
          {(results.length > 0 || (searching && query.trim().length >= 2)) && (
            <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="societe-guest-search-results">
              {searching && results.length === 0 && <div className="px-2 py-1.5 text-[11px] text-neutral-400">Recherche…</div>}
              {results.map(r => {
                const taken = guests.some(g => g.id === r.id);
                return (
                  <button key={r.id} type="button" disabled={taken}
                    onClick={() => { pickSuggestion({ ...r, email: '' }); setQuery(''); setResults([]); }}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-emerald-50 flex items-center justify-between gap-2 disabled:opacity-40"
                    data-testid={`societe-guest-search-result-${r.id}`}>
                    <span className="flex items-center gap-1.5"><UserCheck size={12} className="text-emerald-600" />{r.first_name} {r.last_name}</span>
                  </button>
                );
              })}
              {!searching && results.length === 0 && query.trim().length >= 2 && <div className="px-2 py-1.5 text-[11px] text-neutral-400">Aucun compte trouvé</div>}
            </div>
          )}
        </div>
      )}
      {suggestions.length > 0 && (
        <div>
          <label className="text-[11px] font-medium text-neutral-500 block mb-1">Invités précédents (cliquer pour ajouter)</label>
          <div className="flex flex-wrap gap-1">
            {suggestions.slice(0, 8).map(g => (
              <button key={g.id} type="button" onClick={() => pickSuggestion(g)} disabled={guests.some(x => x.id === g.id)}
                className="text-[11px] px-2 py-1 rounded-full bg-neutral-100 hover:bg-emerald-100 text-neutral-700 hover:text-emerald-800 transition-colors disabled:opacity-40"
                data-testid={`societe-guest-suggestion-${g.id}`}>
                {g.first_name} {g.last_name}
              </button>
            ))}
          </div>
        </div>
      )}
      {guests.map((g, i) => (
        <div key={i} className="rounded-lg border border-neutral-200 p-2 space-y-1.5" data-testid={`societe-guest-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-neutral-600 flex items-center gap-1"><Users size={11} /> Invité {i + 1}{!ready ? <span className="text-neutral-400 font-normal"> · vérification des invitations…</span> : i >= remainingGuests ? <span className="text-amber-700 font-normal"> · Green Fee payant</span> : <span className="text-emerald-700 font-normal"> · offert</span>}</span>
            {(guests.length > 1 || g.first_name || g.last_name) && (
              <button type="button" onClick={() => removeGuest(i)} className="p-0.5 rounded hover:bg-neutral-100" data-testid={`societe-guest-remove-${i}`}><X size={13} className="text-neutral-400" /></button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="text" placeholder="Prénom *" value={g.first_name} onChange={e => update(i, 'first_name', e.target.value)} className="w-full h-8 text-sm border rounded px-2" data-testid={`societe-guest-first-name-${i}`} />
            <input type="text" placeholder="Nom *" value={g.last_name} onChange={e => update(i, 'last_name', e.target.value)} className="w-full h-8 text-sm border rounded px-2" data-testid={`societe-guest-last-name-${i}`} />
            <input type="email" placeholder="Email" value={g.email} onChange={e => update(i, 'email', e.target.value)} className="w-full h-8 text-sm border rounded px-2" data-testid={`societe-guest-email-${i}`} />
            <input type="tel" placeholder="Téléphone" value={g.phone} onChange={e => update(i, 'phone', e.target.value)} className="w-full h-8 text-sm border rounded px-2" data-testid={`societe-guest-phone-${i}`} />
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button type="button" onClick={addGuest} disabled={!canAdd} className="inline-flex items-center gap-1 text-[11px] font-semibold disabled:opacity-40" style={{ color: GREEN }} data-testid="societe-guest-add">
          <Plus size={12} /> Ajouter un invité
        </button>
        <span className="text-[10px] text-neutral-500" data-testid="societe-guest-count">{!ready ? 'Vérification du droit de jeu…' : `${guests.length}/${maxGuests} invité${maxGuests > 1 ? 's' : ''} ${remainingGuests > 0 ? `· ${remainingGuests} offert${remainingGuests > 1 ? 's' : ''} ce jour` : '· plus d\'invitation offerte ce jour'}`}</span>
      </div>
      <p className="text-[10px] text-neutral-500 italic">Les invités sont mémorisés pour vos prochaines réservations.</p>
    </div>
  );
};

export const PartnerChips = ({ partners, selectedIds, onPick, onToggleFavorite }) => {
  if (!partners?.length) return null;
  return (
    <div data-testid="partner-chips">
      <label className="text-[11px] font-medium text-neutral-500 block mb-1">Vos partenaires (un clic pour les ajouter · ★ = favori épinglé)</label>
      <div className="flex flex-wrap gap-1">
        {partners.map(p => (
          <span key={p.id} className="inline-flex items-center rounded-full bg-neutral-100 text-neutral-700 text-[11px] overflow-hidden">
            <button type="button" disabled={selectedIds.includes(p.id)} onClick={() => onPick(p)}
              className="pl-2 pr-1 py-1 hover:bg-emerald-100 hover:text-emerald-800 transition-colors disabled:opacity-40"
              title={p.times ? `${p.times} parties ensemble` : 'Partenaire favori'} data-testid={`partner-chip-${p.id}`}>
              {p.first_name} {p.last_name}{p.times ? <span className="text-neutral-400"> ×{p.times}</span> : null}
            </button>
            <button type="button" onClick={() => onToggleFavorite?.(p)} className="px-1.5 py-1 hover:bg-amber-100 transition-colors"
              title={p.favorite ? 'Retirer des favoris' : 'Épingler en favori'} data-testid={`partner-fav-${p.id}`}>
              <Star size={12} className={p.favorite ? 'fill-amber-400 text-amber-500' : 'text-neutral-400'} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export const FavoriteStar = ({ active, onClick, testId }) => (
  <button type="button" onClick={onClick} className="p-1 rounded hover:bg-amber-50" title={active ? 'Retirer des favoris' : 'Épingler en favori : il apparaîtra à chaque réservation'} data-testid={testId}>
    <Star size={14} className={active ? 'fill-amber-400 text-amber-500' : 'text-neutral-400'} />
  </button>
);
